import {ShiftTasksSummary, useShiftTasksSummary} from '@domain/tasks'
import {DataContentWrapper} from '@hconnect/common/containers'
import {CardBox, SummaryScores, type SummaryScoreProps} from '@hconnect/uikit/src/lib2'
import {spawnErrorToast} from '@hm/fundamental'
import {Box, SxProps, Theme, Typography} from '@mui/material'
import {TFunction} from 'i18next'
import {Moment} from 'moment-timezone'
import {useCallback} from 'react'
import {useTranslation} from 'react-i18next'

import {SHIFT_TASKS_WIDGET_NAMESPACE} from '../consts'

type Props = {
  plantId: string
  from?: Moment
  to?: Moment
  cardBoxSx?: SxProps<Theme>
  placeholderWrapperSx?: SxProps<Theme>
}

const getSummaryScoreItems = (t: TFunction, data?: ShiftTasksSummary): SummaryScoreProps[] => {
  return data
    ? [
        {
          label: t('shiftTasksSummary.label.open'),
          value: data.open,
          valueVariant: 'neutral',
          showTooltip: data.open > 0,
          tooltip: t('shiftTasksSummary.tooltip.open'),
          dataTestId: 'shift-task-open'
        },
        {
          label: t('shiftTasksSummary.label.done'),
          value: data.done,
          valueVariant: 'neutral',
          showTooltip: false,
          dataTestId: 'shift-task-done'
        },
        {
          label: t('shiftTasksSummary.label.overdue'),
          value: data.overdue,
          valueVariant: data.overdue > 0 ? 'negative' : 'neutral',
          showTooltip: data.overdue > 0,
          tooltip: t('shiftTasksSummary.tooltip.overdue'),
          dataTestId: 'shift-task-overdue'
        }
      ]
    : []
}

export const ShiftTasksSummaryContainer: React.FC<Props> = ({
  plantId,
  from,
  to,
  cardBoxSx,
  placeholderWrapperSx
}) => {
  const {t} = useTranslation(SHIFT_TASKS_WIDGET_NAMESPACE)
  const isTimeRangeSupported = !!from && !!to

  const notifyFailedToFetchShiftTasks = useCallback(
    () => spawnErrorToast(t('shiftTasksSummary.message.failedToFetchShiftTasks')),
    [t]
  )

  const {data, isLoading, isError, isRefetching, refetch} = useShiftTasksSummary(
    {
      plantId,
      startDate: from?.toISOString(),
      endDate: to?.toISOString()
    },
    {enabled: isTimeRangeSupported, onError: notifyFailedToFetchShiftTasks}
  )

  const items = getSummaryScoreItems(t, data)
  return (
    <CardBox sx={cardBoxSx} data-test-id="shift-summary-card">
      <Box display="flex" flexDirection="row">
        <Typography variant="h3" mb={2}>
          {t('shiftTasksSummary.cardTitle')}
        </Typography>
      </Box>
      <DataContentWrapper
        showLoader={isLoading}
        isError={isError}
        isRefetching={isRefetching}
        data={data}
        retryFunction={() => void refetch()}
        progressSx={{color: 'primary.main'}}
        placeholderWrapperSx={placeholderWrapperSx}
        noDataSettings={{
          ...(!isTimeRangeSupported && {
            noDataMessage: t('shiftTasksSummary.message.unsupportedTimeRange')
          })
        }}
      >
        <SummaryScores items={items} />
      </DataContentWrapper>
    </CardBox>
  )
}
